<template>
  <div class="games-list" data-t="games-list">
    <slot name="header"></slot>
    <GamesGrid :game-cards="games" :show-player-count="showGamesCounter" />
    <GamesGridSkeleton v-if="!isReadyFirstChunk && isLoading" />

    <slot v-if="!games.length && isReadyFirstChunk" name="empty" />
    <template v-if="total">
      <div ref="endOfTheList" class="end-of-list" />
      <div class="total-info" data-t="total-info-sf0s">
        <div class="total" :class="{ 'visible ': games.length }">
          {{ t('gamesCounter', { current: games.length, total }) }}
        </div>
        <StIcon v-if="isLoading" class="loading" name="loading" />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { useInfiniteScroll } from '@st/use/composables'
import { delay } from '@st/utils'
import type { BaseSortTypeGames } from '../../composables/useSelectSorting'
import type { CasinoGameFindParams } from '../../types'

export interface RequestOrderBy {
  fieldName: BaseSortTypeGames | 'markedAsFavoriteAt' | 'weight' | 'startedAt'
  sortOrder: 'ASC' | 'DESC'
}
</script>

<script lang="ts" setup>
const { t } = useI18n()
const endOfTheList = ref<HTMLDivElement>()

const emit = defineEmits<{
  (e: 'gamesCounter', value: number): void
  (e: 'allGamesForbidden', value: boolean): void
}>()

const props = withDefaults(
  defineProps<{
    requestParams?: CasinoGameFindParams
    requestOrderBy?: RequestOrderBy[]
    showGamesCounter?: boolean
    forAllPlarforms?: boolean
    url?:
      | '/casino/game/find'
      | '/casino/game/recent/find'
      | '/casino/game/favorite/find'
  }>(),
  {
    requestParams: () => ({}),
    requestOrderBy: () => [
      {
        sortOrder: 'DESC',
        fieldName: 'weightInCategory',
        showGamesCounter: false,
      },
    ],
    url: '/casino/game/find',
    forAllPlarforms: false,
  },
)

const allPlatformRequest = computed(() =>
  props.forAllPlarforms ? undefined : { forDesktop: true },
)

const {
  items: games,
  canLoadMore,
  loadMore,
  total,
  isLoading,
  isReadyFirstChunk,
  reset,
} = useInfiniteList({
  url: computed(() => props.url),
  orderBy: () => props.requestOrderBy,
  filterBy: () => ({ ...props.requestParams, ...allPlatformRequest.value }),
  perPage: 20,
})

useInfiniteScroll({
  el: endOfTheList,
  loadMore: async () => {
    if (canLoadMore.value) {
      await loadMore()
    }
  },
  disabledObserver: computed(() => !canLoadMore.value),
  options: { rootMargin: '800px', threshold: 0 },
})

const allGamesForbidden = computed(() =>
  games.value.every((game) => game.forbiddenByCountry),
)

/**
 * Вотчит кол-во игр и проверяет, все ли игры запрещены в стране
 */
watchEffect(() => {
  if (isReadyFirstChunk.value) {
    emit('gamesCounter', total.value ?? 0)
    emit('allGamesForbidden', allGamesForbidden.value)
  }
})

watch(
  () => props.url,
  () => {
    reset()
  },
)

const loadingState = defineModel<'loading' | 'idle'>('loadingState')
watch(
  () => isLoading.value,
  (newValue) => {
    loadingState.value = newValue ? 'loading' : 'idle'
  },
)

async function scrollToTop() {
  if (games.value.length) return
  await delay(0)
  window.scrollTo({
    top: 0,
  })
}

onMounted(() => scrollToTop())
</script>

<style scoped>
.total-info {
  position: relative;
  margin-top: var(--spacing-300);
  margin-bottom: var(--spacing-600);
}

.loading {
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
}

.total {
  margin-top: var(--spacing-100);

  font: var(--desktop-text-xs-medium);
  color: var(--text-tertiary);
  text-align: center;

  opacity: 0;

  transition: opacity 0.3s ease-in-out;
}

.visible {
  opacity: 1;
}
</style>
